import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { getAdminId } from '../../utils/getAdminId';
import type { Warehouse, WarehouseZone, WarehouseRack, WarehouseBin } from '../../types/warehouse';
import {
  MapPin,
  Building2,
  Layers,
  Grid3X3,
  Box,
  ChevronRight,
  Search,
  X,
  Loader2,
  AlertCircle,
} from 'lucide-react';

interface LocationPickerProps {
  value?: string;
  onChange: (location: string, binDetails?: BinDetails) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export interface BinDetails {
  warehouseId: string;
  warehouseName: string;
  zoneId?: string;
  zoneCode?: string;
  rackId: string;
  rackCode: string;
  binId: string;
  binCode: string;
  fullPath: string;
}

interface LocationData {
  warehouses: Warehouse[];
  zones: WarehouseZone[];
  racks: WarehouseRack[];
  bins: WarehouseBin[];
}

export const LocationPicker: React.FC<LocationPickerProps> = ({
  value,
  onChange,
  placeholder = 'Select location...',
  disabled = false,
  className = '',
}) => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<LocationData>({
    warehouses: [],
    zones: [],
    racks: [],
    bins: [],
  });

  const [selectedWarehouse, setSelectedWarehouse] = useState<string | null>(null);
  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  const [selectedRack, setSelectedRack] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const adminId = useMemo(() => getAdminId(user), [user]);

  const loadLocationData = useCallback(async () => {
    if (!adminId) {
      setError('Unable to determine account. Please log in again.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [warehousesRes, zonesRes, racksRes, binsRes] = await Promise.all([
        supabase
          .from('warehouses')
          .select('*')
          .eq('admin_id', adminId)
          .eq('is_archived', false)
          .order('name'),
        supabase
          .from('warehouse_zones')
          .select('*')
          .eq('admin_id', adminId)
          .order('zone_code'),
        supabase
          .from('warehouse_racks')
          .select('*')
          .eq('admin_id', adminId)
          .order('rack_code'),
        supabase
          .from('warehouse_bins')
          .select('*')
          .eq('admin_id', adminId)
          .order('sort_order'),
      ]);

      if (warehousesRes.error) throw warehousesRes.error;
      if (zonesRes.error) throw zonesRes.error;
      if (racksRes.error) throw racksRes.error;
      if (binsRes.error) throw binsRes.error;

      setData({
        warehouses: warehousesRes.data || [],
        zones: zonesRes.data || [],
        racks: racksRes.data || [],
        bins: binsRes.data || [],
      });

      if (warehousesRes.data?.length === 1) {
        setSelectedWarehouse(warehousesRes.data[0].id);
      }
    } catch (err) {
      console.error('Failed to load location data:', err);
      setError('Failed to load warehouse data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [adminId]);

  useEffect(() => {
    if (isOpen && adminId) {
      loadLocationData();
    }
  }, [isOpen, adminId, loadLocationData]);

  const filteredZones = useMemo(() => {
    if (!selectedWarehouse) return [];
    return data.zones.filter((z) => z.warehouse_id === selectedWarehouse);
  }, [data.zones, selectedWarehouse]);

  const filteredRacks = useMemo(() => {
    if (!selectedWarehouse) return [];
    let racks = data.racks.filter((r) => r.warehouse_id === selectedWarehouse);

    if (selectedZone) {
      racks = racks.filter((r) => r.zone_id === selectedZone);
    }

    return racks;
  }, [data.racks, selectedWarehouse, selectedZone]);

  const filteredBins = useMemo(() => {
    if (!selectedRack) return [];
    return data.bins.filter((b) => b.rack_id === selectedRack);
  }, [data.bins, selectedRack]);

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];

    const query = searchQuery.toLowerCase();
    const results: BinDetails[] = [];

    for (const bin of data.bins) {
      const rack = data.racks.find((r) => r.id === bin.rack_id);
      if (!rack) continue;

      const warehouse = data.warehouses.find((w) => w.id === rack.warehouse_id);
      if (!warehouse) continue;

      const zone = rack.zone_id ? data.zones.find((z) => z.id === rack.zone_id) : null;

      const fullPath = zone
        ? `${warehouse.name} > ${zone.zone_code} > ${rack.rack_code} > ${bin.bin_code}`
        : `${warehouse.name} > ${rack.rack_code} > ${bin.bin_code}`;

      if (
        bin.bin_code.toLowerCase().includes(query) ||
        rack.rack_code.toLowerCase().includes(query) ||
        (zone?.zone_code.toLowerCase().includes(query)) ||
        warehouse.name.toLowerCase().includes(query) ||
        fullPath.toLowerCase().includes(query)
      ) {
        results.push({
          warehouseId: warehouse.id,
          warehouseName: warehouse.name,
          zoneId: zone?.id,
          zoneCode: zone?.zone_code,
          rackId: rack.id,
          rackCode: rack.rack_code,
          binId: bin.id,
          binCode: bin.bin_code,
          fullPath,
        });
      }
    }

    return results.slice(0, 20);
  }, [searchQuery, data]);

  const handleSelectBin = (bin: WarehouseBin) => {
    const rack = data.racks.find((r) => r.id === bin.rack_id);
    if (!rack) return;

    const warehouse = data.warehouses.find((w) => w.id === rack.warehouse_id);
    if (!warehouse) return;

    const zone = rack.zone_id ? data.zones.find((z) => z.id === rack.zone_id) : null;

    const fullPath = zone
      ? `${warehouse.name} > ${zone.zone_code} > ${rack.rack_code} > ${bin.bin_code}`
      : `${warehouse.name} > ${rack.rack_code} > ${bin.bin_code}`;

    const binDetails: BinDetails = {
      warehouseId: warehouse.id,
      warehouseName: warehouse.name,
      zoneId: zone?.id,
      zoneCode: zone?.zone_code,
      rackId: rack.id,
      rackCode: rack.rack_code,
      binId: bin.id,
      binCode: bin.bin_code,
      fullPath,
    };

    onChange(fullPath, binDetails);
    setIsOpen(false);
    resetSelection();
  };

  const handleSelectSearchResult = (result: BinDetails) => {
    onChange(result.fullPath, result);
    setIsOpen(false);
    resetSelection();
  };

  const resetSelection = () => {
    setSelectedWarehouse(null);
    setSelectedZone(null);
    setSelectedRack(null);
    setSearchQuery('');
    setError(null);
  };

  const handleClear = () => {
    onChange('');
    resetSelection();
  };

  const selectedWarehouseData = data.warehouses.find((w) => w.id === selectedWarehouse);
  const selectedZoneData = data.zones.find((z) => z.id === selectedZone);
  const selectedRackData = data.racks.find((r) => r.id === selectedRack);

  return (
    <div className={`relative ${className}`}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && setIsOpen(true)}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            setIsOpen(true);
          }
        }}
        aria-disabled={disabled}
        className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-colors text-left cursor-pointer ${
          disabled
            ? 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700 cursor-not-allowed'
            : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500'
        }`}
      >
        <div className="flex items-center gap-2 overflow-hidden">
          <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0" />
          {value ? (
            <span className="text-gray-900 dark:text-gray-100 truncate">{value}</span>
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
        </div>
        {value && !disabled && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-600"
          >
            <X className="h-4 w-4 text-gray-400" />
          </button>
        )}
      </div>

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => {
              setIsOpen(false);
              resetSelection();
            }}
          />

          <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">Select Location</h2>
                    <p className="text-blue-100 text-sm">Choose a bin location for this item</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setIsOpen(false);
                    resetSelection();
                  }}
                  className="p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by bin code, rack, zone, or warehouse..."
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center py-12 px-4">
                  <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
                    <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 text-center mb-4">{error}</p>
                  <button
                    onClick={loadLocationData}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              ) : searchQuery.trim() ? (
                <div className="p-4">
                  {searchResults.length > 0 ? (
                    <div className="space-y-2">
                      {searchResults.map((result) => (
                        <button
                          key={result.binId}
                          onClick={() => handleSelectSearchResult(result)}
                          className="w-full flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-left"
                        >
                          <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Box className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 dark:text-gray-100">
                              {result.binCode}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                              {result.fullPath}
                            </div>
                          </div>
                          <ChevronRight className="h-5 w-5 text-gray-400" />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Search className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500 dark:text-gray-400">
                        No locations found matching "{searchQuery}"
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-4 space-y-4">
                  {selectedRack && filteredBins.length > 0 && (
                    <Breadcrumb
                      warehouse={selectedWarehouseData}
                      zone={selectedZoneData}
                      rack={selectedRackData}
                      onNavigate={(level) => {
                        if (level === 'warehouse') {
                          setSelectedZone(null);
                          setSelectedRack(null);
                        } else if (level === 'zone') {
                          setSelectedRack(null);
                        } else if (level === 'rack') {
                          setSelectedRack(null);
                        }
                      }}
                    />
                  )}

                  {!selectedWarehouse && (
                    <div className="space-y-2">
                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        Select Warehouse
                      </h3>
                      {data.warehouses.length > 0 ? (
                        <div className="grid gap-2">
                          {data.warehouses.map((warehouse) => (
                            <button
                              key={warehouse.id}
                              onClick={() => setSelectedWarehouse(warehouse.id)}
                              className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-left"
                            >
                              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                                <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                              </div>
                              <div className="flex-1">
                                <div className="font-medium text-gray-900 dark:text-gray-100">
                                  {warehouse.name}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {warehouse.length} x {warehouse.width} {warehouse.unit}
                                </div>
                              </div>
                              <ChevronRight className="h-5 w-5 text-gray-400" />
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                          <p className="text-gray-500 dark:text-gray-400">No warehouses available</p>
                          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                            Create a warehouse in Settings first
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedWarehouse && !selectedRack && (
                    <div className="space-y-4">
                      {filteredZones.length > 0 && !selectedZone && (
                        <div className="space-y-2">
                          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                            <Layers className="h-4 w-4" />
                            Select Zone (Optional)
                          </h3>
                          <div className="grid gap-2">
                            {filteredZones.map((zone) => (
                              <button
                                key={zone.id}
                                onClick={() => setSelectedZone(zone.id)}
                                className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-left"
                              >
                                <div
                                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                                  style={{ backgroundColor: `${zone.color}20` }}
                                >
                                  <Layers className="h-5 w-5" style={{ color: zone.color }} />
                                </div>
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900 dark:text-gray-100">
                                    Zone {zone.zone_code}
                                  </div>
                                  {zone.zone_name && (
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                      {zone.zone_name}
                                    </div>
                                  )}
                                </div>
                                <ChevronRight className="h-5 w-5 text-gray-400" />
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="space-y-2">
                        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                          <Grid3X3 className="h-4 w-4" />
                          Select Rack
                        </h3>
                        {filteredRacks.length > 0 ? (
                          <div className="grid gap-2">
                            {filteredRacks.map((rack) => {
                              const zone = data.zones.find((z) => z.id === rack.zone_id);
                              return (
                                <button
                                  key={rack.id}
                                  onClick={() => setSelectedRack(rack.id)}
                                  className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-left"
                                >
                                  <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
                                    <Grid3X3 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                      {rack.rack_code}
                                      {zone && (
                                        <span
                                          className="w-2.5 h-2.5 rounded-full"
                                          style={{ backgroundColor: zone.color }}
                                        />
                                      )}
                                    </div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                      {rack.levels} levels x {rack.bays} bays
                                      {rack.rack_name && ` - ${rack.rack_name}`}
                                    </div>
                                  </div>
                                  <ChevronRight className="h-5 w-5 text-gray-400" />
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <Grid3X3 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500 dark:text-gray-400">
                              No racks available
                              {selectedZone && ' in this zone'}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {selectedRack && (
                    <div className="space-y-2">
                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                        <Box className="h-4 w-4" />
                        Select Bin
                      </h3>
                      {filteredBins.length > 0 ? (
                        <BinGrid
                          bins={filteredBins}
                          rack={selectedRackData!}
                          onSelect={handleSelectBin}
                        />
                      ) : (
                        <div className="text-center py-8">
                          <Box className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                          <p className="text-gray-700 dark:text-gray-300 font-medium">No bins configured</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-xs mx-auto">
                            Generate bins for this rack in Settings &gt; Warehouses &gt; Racks tab
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface BreadcrumbProps {
  warehouse?: Warehouse;
  zone?: WarehouseZone;
  rack?: WarehouseRack;
  onNavigate: (level: 'warehouse' | 'zone' | 'rack') => void;
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ warehouse, zone, rack, onNavigate }) => (
  <div className="flex items-center gap-2 text-sm flex-wrap">
    {warehouse && (
      <>
        <button
          onClick={() => onNavigate('warehouse')}
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          {warehouse.name}
        </button>
        <ChevronRight className="h-4 w-4 text-gray-400" />
      </>
    )}
    {zone && (
      <>
        <button
          onClick={() => onNavigate('zone')}
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          Zone {zone.zone_code}
        </button>
        <ChevronRight className="h-4 w-4 text-gray-400" />
      </>
    )}
    {rack && (
      <button
        onClick={() => onNavigate('rack')}
        className="text-blue-600 dark:text-blue-400 hover:underline"
      >
        {rack.rack_code}
      </button>
    )}
  </div>
);

interface BinGridProps {
  bins: WarehouseBin[];
  rack: WarehouseRack;
  onSelect: (bin: WarehouseBin) => void;
}

const BinGrid: React.FC<BinGridProps> = ({ bins, rack, onSelect }) => {
  const binGrid = useMemo(() => {
    const grid: (WarehouseBin | null)[][] = [];

    for (let level = 1; level <= rack.levels; level++) {
      const row: (WarehouseBin | null)[] = [];
      for (let bay = 1; bay <= rack.bays; bay++) {
        const bin = bins.find((b) => b.level_number === level && b.bay_index === bay);
        row.push(bin || null);
      }
      grid.push(row);
    }

    return grid;
  }, [bins, rack.levels, rack.bays]);

  const bayLetters = useMemo(() => {
    const letters: string[] = [];
    for (let i = 0; i < rack.bays; i++) {
      letters.push(String.fromCharCode(65 + i));
    }
    return letters;
  }, [rack.bays]);

  return (
    <div className="overflow-x-auto">
      <div className="inline-block min-w-full">
        <div className="flex items-end gap-1 mb-2 pl-10">
          {bayLetters.map((letter) => (
            <div
              key={letter}
              className="w-14 text-center text-xs font-medium text-gray-500 dark:text-gray-400"
            >
              {letter}
            </div>
          ))}
        </div>

        {binGrid.map((row, rowIndex) => {
          const levelNumber = rowIndex + 1;
          return (
            <div key={rowIndex} className="flex items-center gap-1 mb-1">
              <div className="w-8 text-right text-xs font-medium text-gray-500 dark:text-gray-400 pr-1">
                {levelNumber}
              </div>
              {row.map((bin, colIndex) =>
                bin ? (
                  <button
                    key={colIndex}
                    onClick={() => onSelect(bin)}
                    className="w-14 h-10 rounded border-2 border-emerald-400 dark:border-emerald-600 bg-emerald-50 dark:bg-emerald-900/20 hover:border-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors flex items-center justify-center"
                    title={bin.bin_code}
                  >
                    <span className="text-[10px] font-medium text-gray-700 dark:text-gray-300 truncate px-0.5">
                      {bin.bin_code.split('-').pop()}
                    </span>
                  </button>
                ) : (
                  <div
                    key={colIndex}
                    className="w-14 h-10 rounded border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50"
                  />
                )
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export function formatLocationPath(
  warehouseName: string,
  zoneCode?: string,
  rackCode?: string,
  binCode?: string
): string {
  const parts = [warehouseName];
  if (zoneCode) parts.push(zoneCode);
  if (rackCode) parts.push(rackCode);
  if (binCode) parts.push(binCode);
  return parts.join(' > ');
}
